import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileSectionImage from '../assets/profile-section-Image.png';
import { API_CONFIG } from '../config/api';

interface BloodTestData {
  testDate: string;
  weight: string;
  systolic: string;
  diastolic: string;
  hba1c: string;
  totalCholesterol: string;
  ldl: string;
  hdl: string;
  nonHdl: string;
  triglycerides: string;
  measurementUnit: 'mg/dL' | 'mmol/L';
}

interface BloodTestApiData {
  testDate: string;
  weight: number;
  bloodPressure?: {
    systolic?: number;
    diastolic?: number;
  };
  bloodSugar?: {
    hba1c?: number;
  };
  cholesterol?: {
    measurementUnit: string;
    total?: number;
    ldl?: number;
    hdl?: number;
    nonHdl?: number;
    triglycerides?: number;
  };
}

const BloodTestInputPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<BloodTestData>({
    testDate: '',
    weight: '',
    systolic: '',
    diastolic: '',
    hba1c: '',
    totalCholesterol: '',
    ldl: '',
    hdl: '',
    nonHdl: '',
    triglycerides: '',
    measurementUnit: 'mg/dL'
  });

  const [errors, setErrors] = useState<Partial<BloodTestData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof BloodTestData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<BloodTestData> = {};

    if (!formData.testDate.trim()) {
      newErrors.testDate = 'Test date is required';
    }

    if (!formData.weight.trim()) {
      newErrors.weight = 'Weight is required';
    } else if (isNaN(Number(formData.weight)) || Number(formData.weight) <= 0) {
      newErrors.weight = 'Please enter a valid weight';
    }

    if (formData.systolic && (isNaN(Number(formData.systolic)) || Number(formData.systolic) <= 0)) {
      newErrors.systolic = 'Please enter a valid systolic value';
    }

    if (formData.diastolic && (isNaN(Number(formData.diastolic)) || Number(formData.diastolic) <= 0)) {
      newErrors.diastolic = 'Please enter a valid diastolic value';
    }

    if (formData.hba1c && (isNaN(Number(formData.hba1c)) || Number(formData.hba1c) <= 0)) {
      newErrors.hba1c = 'Please enter a valid HbA1c value';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare data for API
      const bloodTestData: BloodTestApiData = {
        testDate: formData.testDate,
        weight: parseFloat(formData.weight)
      };

      // Add blood pressure if provided
      if (formData.systolic || formData.diastolic) {
        bloodTestData.bloodPressure = {};
        if (formData.systolic) {
          bloodTestData.bloodPressure.systolic = parseFloat(formData.systolic);
        }
        if (formData.diastolic) {
          bloodTestData.bloodPressure.diastolic = parseFloat(formData.diastolic);
        }
      }

      // Add blood sugar if provided
      if (formData.hba1c) {
        bloodTestData.bloodSugar = {
          hba1c: parseFloat(formData.hba1c)
        };
      }

      // Add cholesterol if any values provided
      const cholesterolValues = [formData.totalCholesterol, formData.ldl, formData.hdl, formData.nonHdl, formData.triglycerides];
      const hasCholesterolValues = cholesterolValues.some(value => value && value.trim() !== '');
      
      if (hasCholesterolValues) {
        bloodTestData.cholesterol = {
          measurementUnit: formData.measurementUnit
        };
        
        if (formData.totalCholesterol) {
          bloodTestData.cholesterol.total = parseFloat(formData.totalCholesterol);
        }
        if (formData.ldl) {
          bloodTestData.cholesterol.ldl = parseFloat(formData.ldl);
        }
        if (formData.hdl) {
          bloodTestData.cholesterol.hdl = parseFloat(formData.hdl);
        }
        if (formData.nonHdl) {
          bloodTestData.cholesterol.nonHdl = parseFloat(formData.nonHdl);
        }
        if (formData.triglycerides) {
          bloodTestData.cholesterol.triglycerides = parseFloat(formData.triglycerides);
        }
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.ENDPOINTS.BLOOD_TESTS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(bloodTestData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('Blood test data saved successfully!');
        navigate('/panel/learn/');
      } else {
        throw new Error(result.message || 'Failed to save blood test data');
      }
    } catch (error) {

      alert(`Error saving blood test: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/panel/learn/');
  };

  return (
    <div className="blood-test-input">
      <div className="blood-test-input__container">
        {/* Left Panel - Visual */}
        <div className="blood-test-input__left">
          <div className="blood-test-input__background">
            <img 
              src={ProfileSectionImage} 
              alt="Blood Test Input Background" 
              className="blood-test-input__bg-image"
            />
          </div>
          
          <div className="blood-test-input__visual">
            <h1 className="blood-test-input__title">Manual Test Reporting</h1>
            <div className="blood-test-input__subtitle">
              Please ensure that the data entered is correct. This data is not editable once you confirm it
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="blood-test-input__right">
          <form onSubmit={handleSubmit} className="blood-test-input__form">
            <div className="blood-test-input__form-content">
              
              {/* Test Date */}
              <div className="blood-test-input__field-group">
                <label className="blood-test-input__label">Test Date</label>
                <input
                  type="date"
                  value={formData.testDate}
                  onChange={(e) => handleInputChange('testDate', e.target.value)}
                  className={`blood-test-input__input ${errors.testDate ? 'blood-test-input__input--error' : ''}`}
                  max={new Date().toISOString().split('T')[0]}
                />
                {errors.testDate && <span className="blood-test-input__error">{errors.testDate}</span>}
              </div>

              {/* Weight */}
              <div className="blood-test-input__field-group">
                <label className="blood-test-input__label">Weight (kg)</label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  className={`blood-test-input__input ${errors.weight ? 'blood-test-input__input--error' : ''}`}
                  placeholder="Enter weight"
                  step="0.1"
                  min="0"
                />
                {errors.weight && <span className="blood-test-input__error">{errors.weight}</span>}
              </div>

              {/* Blood Pressure Section */}
              <div className="blood-test-input__section">
                <h3 className="blood-test-input__section-title">BLOOD PRESSURE</h3>
                
                <div className="blood-test-input__field-row">
                  <div className="blood-test-input__field-group">
                    <label className="blood-test-input__label">Systolic (mmHg)</label>
                    <input
                      type="number"
                      value={formData.systolic}
                      onChange={(e) => handleInputChange('systolic', e.target.value)}
                      className={`blood-test-input__input ${errors.systolic ? 'blood-test-input__input--error' : ''}`}
                      placeholder="120"
                      min="0"
                    />
                    {errors.systolic && <span className="blood-test-input__error">{errors.systolic}</span>}
                  </div>

                  <div className="blood-test-input__field-group">
                    <label className="blood-test-input__label">Diastolic (mmHg)</label>
                    <input
                      type="number"
                      value={formData.diastolic}
                      onChange={(e) => handleInputChange('diastolic', e.target.value)}
                      className={`blood-test-input__input ${errors.diastolic ? 'blood-test-input__input--error' : ''}`}
                      placeholder="80"
                      min="0"
                    />
                    {errors.diastolic && <span className="blood-test-input__error">{errors.diastolic}</span>}
                  </div>
                </div>
              </div>

              {/* Blood Sugar Section */}
              <div className="blood-test-input__section">
                <h3 className="blood-test-input__section-title">BLOOD SUGAR</h3>
                
                <div className="blood-test-input__field-group">
                  <label className="blood-test-input__label">HbA1c (%)</label>
                  <input
                    type="number"
                    value={formData.hba1c}
                    onChange={(e) => handleInputChange('hba1c', e.target.value)}
                    className={`blood-test-input__input ${errors.hba1c ? 'blood-test-input__input--error' : ''}`}
                    placeholder="5.7"
                    step="0.1"
                    min="0"
                  />
                  {errors.hba1c && <span className="blood-test-input__error">{errors.hba1c}</span>}
                </div>
              </div>

              {/* Cholesterol Section */}
              <div className="blood-test-input__section">
                <h3 className="blood-test-input__section-title">CHOLESTEROL</h3>
                
                <div className="blood-test-input__field-group">
                  <label className="blood-test-input__label">
                    Please indicate the measurement unit shown in your blood test report
                  </label>
                  <div className="blood-test-input__radio-group">
                    <label className="blood-test-input__radio">
                      <input
                        type="radio"
                        name="measurementUnit"
                        value="mg/dL"
                        checked={formData.measurementUnit === 'mg/dL'}
                        onChange={(e) => handleInputChange('measurementUnit', e.target.value)}
                      />
                      <span>mg/dL</span>
                    </label>
                    <label className="blood-test-input__radio">
                      <input
                        type="radio"
                        name="measurementUnit"
                        value="mmol/L"
                        checked={formData.measurementUnit === 'mmol/L'}
                        onChange={(e) => handleInputChange('measurementUnit', e.target.value)}
                      />
                      <span>mmol/L</span>
                    </label>
                  </div>
                </div>

                <div className="blood-test-input__field-row">
                  <div className="blood-test-input__field-group">
                    <label className="blood-test-input__label">Total</label>
                    <input
                      type="number"
                      value={formData.totalCholesterol}
                      onChange={(e) => handleInputChange('totalCholesterol', e.target.value)}
                      className="blood-test-input__input"
                      placeholder="200"
                      step="0.1"
                      min="0"
                    />
                  </div>

                  <div className="blood-test-input__field-group">
                    <label className="blood-test-input__label">LDL</label>
                    <input
                      type="number"
                      value={formData.ldl}
                      onChange={(e) => handleInputChange('ldl', e.target.value)}
                      className="blood-test-input__input"
                      placeholder="100"
                      step="0.1"
                      min="0"
                    />
                  </div>
                </div>

                <div className="blood-test-input__field-row">
                  <div className="blood-test-input__field-group">
                    <label className="blood-test-input__label">HDL</label>
                    <input
                      type="number"
                      value={formData.hdl}
                      onChange={(e) => handleInputChange('hdl', e.target.value)}
                      className="blood-test-input__input"
                      placeholder="60"
                      step="0.1"
                      min="0"
                    />
                  </div>

                  <div className="blood-test-input__field-group">
                    <label className="blood-test-input__label">Non-HDL</label>
                    <input
                      type="number"
                      value={formData.nonHdl}
                      onChange={(e) => handleInputChange('nonHdl', e.target.value)}
                      className="blood-test-input__input"
                      placeholder="140"
                      step="0.1"
                      min="0"
                    />
                  </div>
                </div>

                <div className="blood-test-input__field-group">
                  <label className="blood-test-input__label">Triglycerides</label>
                  <input
                    type="number"
                    value={formData.triglycerides}
                    onChange={(e) => handleInputChange('triglycerides', e.target.value)}
                    className="blood-test-input__input"
                    placeholder="150"
                    step="0.1"
                    min="0"
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="blood-test-input__navigation">
                <button type="button" className="blood-test-input__back" onClick={handleBack}>
                <span className="material-symbols-outlined">arrow_back</span>
                  Back
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary blood-test-input__confirm"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Confirm'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BloodTestInputPage;



